import { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Check,
  ChevronDown,
  Plus,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useKintoneEnvironment } from "@/features/app-bindings/hooks/useKintoneEnvironment";

export function CustomerContextMenu() {
  const { t } = useTranslation();
  const { user, switchCustomer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [switchingCustomerId, setSwitchingCustomerId] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const currentMembership = user?.currentMembership ?? null;
  const currentCustomer = useMemo(
    () =>
      user?.memberships.find(
        (membership) => membership.customerId === currentMembership?.customerId,
      ) ?? null,
    [currentMembership?.customerId, user?.memberships],
  );
  const roleLabel = currentMembership
    ? t(`roles.${currentMembership.role}`, currentMembership.role)
    : "";

  const handleCustomerSwitch = async (customerId: string) => {
    if (!user || customerId === currentMembership?.customerId) return;
    setSwitchingCustomerId(customerId);
    setSwitchError(null);
    try {
      const target = user.memberships.find(
        (membership) => membership.customerId === customerId,
      );
      await switchCustomer(customerId);
      if (target?.role !== "customer_admin" && location.pathname.startsWith("/admin")) {
        navigate("/console/dashboard");
      }
    } catch {
      setSwitchError(t("header.switchFailed"));
    } finally {
      setSwitchingCustomerId(null);
    }
  };

  return (
    <div className="border-b px-4 py-3">
      <div className="sidebar-expanded flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Building2 className="h-3.5 w-3.5" />
        <span>{t("header.customerContext")}</span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="-ml-2 mt-1 h-auto w-full min-w-0 justify-between gap-2 px-2 py-1.5 text-left hover:bg-accent"
          >
            <Building2 className="sidebar-compact h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="sidebar-label min-w-0 flex-1 truncate text-sm font-semibold leading-tight text-foreground">
              {currentCustomer?.customerName ?? t("header.customerUnknown")}
            </span>
            <ChevronDown className="sidebar-expanded h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          <DropdownMenuLabel className="space-y-1">
            <span className="block text-xs font-medium text-muted-foreground">
              {t("header.switchCustomer")}
            </span>
            <span className="block truncate text-sm">{user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {user?.memberships.map((membership) => {
            const isCurrent = membership.customerId === currentMembership?.customerId;
            const isSwitching = switchingCustomerId === membership.customerId;
            return (
              <DropdownMenuItem
                key={membership.customerUserId}
                disabled={isCurrent || isSwitching || membership.userState !== "active"}
                onClick={() => void handleCustomerSwitch(membership.customerId)}
                className="items-start gap-3 py-2"
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                    isCurrent
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-transparent",
                  )}
                >
                  <Check className="h-3 w-3" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {membership.customerName}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {t(`roles.${membership.role}`, membership.role)} /{" "}
                    {membership.displayName}
                  </span>
                </span>
              </DropdownMenuItem>
            );
          })}
          {currentMembership?.role === "customer_admin" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate("/console/organization/settings")}
                className="gap-3 py-2"
              >
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span>{t("header.organizationSettings")}</span>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => navigate("/console/organizations/new")}
            className="gap-3 py-2"
          >
            <Plus className="h-4 w-4" />
            <span>{t("header.createCustomer")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="sidebar-expanded mt-1 flex flex-wrap items-center gap-1.5">
        {roleLabel && (
          <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
            <ShieldCheck className="h-3 w-3" />
            {roleLabel}
          </span>
        )}
        {currentMembership?.role === "customer_admin" ? <CurrentPlanStatus /> : null}
      </div>
      {switchError && (
        <div className="mt-1 text-xs font-medium text-destructive">{switchError}</div>
      )}
    </div>
  );
}

function CurrentPlanStatus() {
  const { environment, isLoading } = useKintoneEnvironment();
  if (isLoading || !environment) return null;
  const commercial = environment.commercial;
  const needsAttention =
    commercial.commercialState === "expired" ||
    commercial.commercialState === "payment_required";
  const planLabel = commercial.planKind === "trial"
    ? (commercial.releasePhase === "beta" ? "お試しプラン（ベータ版）" : "お試しプラン")
    : "有料プラン";
  return (
    <Link
      to="/console/service-subscription"
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium hover:underline",
        needsAttention
          ? "bg-amber-100 text-amber-900"
          : "bg-primary/10 text-primary",
      )}
      aria-label={`${planLabel}${commercial.trialDaysRemaining === null ? "" : ` 残り${commercial.trialDaysRemaining}日`}`}
    >
      {planLabel}
      {commercial.trialDaysRemaining !== null ? (
        <strong>残り{commercial.trialDaysRemaining}日</strong>
      ) : needsAttention ? (
        <strong>要確認</strong>
      ) : null}
    </Link>
  );
}
